import React, { useEffect, useState } from "react";
import DocumentDetailFooter from "../DocumentDetailFooter";
import DocumentDetailAttachmentList from "../DocumentDetailAttachmentList";
import {
  DocumentAttachmentDetailDto,
  FreeTagDetailDto,
  TagDetailDto,
  ReferenceUserDto,
} from "apis/data-contracts";
import CreateDocumentFormField from "components/document/Create/CreateDocumentForm/CreateDocumentFormField";
import Input from "components/foundation/Input/Input";
import Button from "components/foundation/Button";
import Modal from "components/foundation/Modal/Modal";
import AnalysisTagEditSelect from "components/document/edlt/AnalysisTagEditSelect";
import WorkTagEditSelect from "components/document/edlt/WorkTagEditSelect";
import useUserDetailQuery from "features/user/hooks/queries/useUserDetailQuery";
import { useDeleteDocumentMutation } from "features/document/hooks/mutations/useDeleteDocumentMutation";
import { useNavigate } from "react-router-dom";
import { ROUTER_PATH } from "config/path";
import Label from "components/foundation/Label/Label";
import styled from "styled-components";
import ReferenceUserEditModal from "components/document/edlt/ReferenceUserEdit";
import useApproveRequest from "features/document/hooks/create/useApproveRequest";
import ApproverCommentField from "components/document/Create/CreateDocumentForm/CreateDocumentFormField/ApproverCommentField";
import AttachedFileEdit from "components/document/edlt/AttachedFileEdit";
import Toast from "components/foundation/Toast";

interface DocumentDetailFooterForRegularUserProps {
  approvedUserName: string;
  approvedUserUuid: string;
  attachments: DocumentAttachmentDetailDto[];
  documentFileUrl: string;
  freeTags: FreeTagDetailDto[];
  tags: TagDetailDto[];
  documentCategory: string;
  approveStatus?: string;
  uuid: string;
  permissionType?: "OWNER" | "APPROVER" | "REFERRER" | "READER";
  referenceUsers?: ReferenceUserDto[];
  onTagUpdateSuccess: () => void;
  approveComment: string;
  author: string;
  uploadDocPdfAndGetS3Url?: (fileName: string) => Promise<string>;
  isAnalysis?: boolean;
}

const DocumentDetailFooterForRegularUser = ({
  approvedUserName,
  approvedUserUuid,
  attachments,
  freeTags,
  tags,
  documentCategory,
  approveStatus,
  uuid,
  permissionType,
  onTagUpdateSuccess,
  referenceUsers,
  approveComment,
  author,
  uploadDocPdfAndGetS3Url,
  isAnalysis,
}: DocumentDetailFooterForRegularUserProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openReferenceUserEditModal, setOpenReferenceUserEditModal] =
    useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [openAttachedFileEditModal, setOpenAttachedFileEditModal] =
    useState(false);
  const [isSuccessToast, setIsSuccessToast] = useState(false);

  const { data: currentUser } = useUserDetailQuery();

  const isSuperAdmin = currentUser?.adminType === "SUPERADMIN";
  const isAdmin = currentUser?.adminType === "ADMIN";
  const isOwner = permissionType === "OWNER";
  const isApprover = permissionType === "APPROVER";
  const isDocumentAuthor = currentUser?.uuid === author;

  // 참조자 수정 : 대표 이사, 작성자 본인
  const hasAuthorityToEditReferenceUser = isSuperAdmin || isOwner;

  // 대표 이사 계정이면서 작성자 본인인 경우
  const isOwnerDocument = isApprover && isSuperAdmin && isDocumentAuthor;
  // 태그 수정 : 결재 완료 문서 + 대표 이사, 관리자, 작성자 본인
  const hasAuthorityToEditTag =
    approveStatus === "APPROVE" && (isSuperAdmin || isAdmin || isOwner);

  // 문서 삭제 : 대표이사 계정
  const hasAuthorityToDelete = isSuperAdmin;

  // 추가 첨부 : 결재 완료 문서 + 작성자 본인
  const hasAuthorityToAddAttachment =
    approveStatus === "APPROVE" && (isOwner || isOwnerDocument);

  // Analysis 문서이면서 문서 최종 완료 이전에 참조자 수정, 태그 수정, 문서 삭제 불가
  const isAnalysisInProgress = isAnalysis && approveStatus !== "APPROVE";

  // 2단계 문서 등록 : 1단계 승인 완료 문서이면서 작성자 본인인 경우
  const canCreate2StepDocument =
    isAnalysis && approveStatus === "REVIEW" && (isOwner || isOwnerDocument);

  const handleCloseModal = (isSuccess?: boolean) => {
    setIsModalOpen(false);
    if (isSuccess && onTagUpdateSuccess) {
      onTagUpdateSuccess();
    }
  };

  // 문서 삭제
  const navigate = useNavigate();

  const { mutate: deleteDocument } = useDeleteDocumentMutation(uuid);
  const handleDeleteDocument = (documentCategory: string) => {
    deleteDocument();

    if (documentCategory === "WORK") {
      navigate(ROUTER_PATH.document.work.dashboard);
    } else if (documentCategory === "ANALYSIS") {
      navigate(ROUTER_PATH.document.analysis.dashboard);
    }
  };

  // 참조자 수정
  const [newReferenceUsersList, setNewReferenceUsersList] = useState<
    ReferenceUserDto[]
  >(referenceUsers || []);
  const [tempReferenceUsersList, setTempReferenceUsersList] = useState<
    ReferenceUserDto[]
  >(referenceUsers || []);

  useEffect(() => {
    setNewReferenceUsersList(referenceUsers || []);
  }, [referenceUsers]);

  const { updateReferenceUser } = useApproveRequest();

  return (
    <>
      <DocumentDetailFooter
        fields={
          <>
            <DocumentDetailAttachmentList
              attachmentList={attachments}
              withoutCard
              approvedDocument={approveStatus === "APPROVE"}
            />

            <CreateDocumentFormField label="전결권자">
              <Input
                value={approvedUserName}
                readOnly
                noBorder={approveStatus === "APPROVE"}
              />
            </CreateDocumentFormField>

            {approveComment && (
              <ApproverCommentField
                comment={approveComment}
                noBorder={approveStatus === "APPROVE"}
              />
            )}

            <CreateDocumentFormField
              label="참조자"
              bodyStyle={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
              }}
            >
              <ReferenceUserWrapStyle>
                {newReferenceUsersList?.map((referenceUser) => (
                  <Label key={referenceUser.uuid} variant="grey">
                    {referenceUser?.name}
                  </Label>
                ))}
              </ReferenceUserWrapStyle>
              {!isAnalysisInProgress && hasAuthorityToEditReferenceUser && (
                <Button
                  variant="tertiary"
                  size="small"
                  fill={false}
                  style={{
                    width: "auto",
                    whiteSpace: "nowrap",
                  }}
                  onClick={() => {
                    setTempReferenceUsersList([...newReferenceUsersList]);
                    setOpenReferenceUserEditModal(true);
                  }}
                >
                  수정
                </Button>
              )}
            </CreateDocumentFormField>
          </>
        }
        actions={
          <>
            {!isAnalysisInProgress && hasAuthorityToAddAttachment ? (
              <Modal.Root
                open={openAttachedFileEditModal}
                onOpenChange={setOpenAttachedFileEditModal}
              >
                <Modal.Trigger>
                  <Button variant="secondary" size="large" className="pc-only">
                    추가 첨부
                  </Button>
                </Modal.Trigger>
                <Modal.Layout>
                  <Modal.Content>
                    <AttachedFileEdit
                      documentUuid={uuid}
                      attachmentsLength={attachments.length}
                      onClose={() => {
                        setOpenAttachedFileEditModal(false);
                      }}
                      onSuccess={() => {
                        setIsSuccessToast(true);
                      }}
                      uploadDocPdfAndGetS3Url={uploadDocPdfAndGetS3Url}
                    />
                  </Modal.Content>
                </Modal.Layout>
              </Modal.Root>
            ) : undefined}
            {canCreate2StepDocument ? (
              <Button
                variant="primary"
                size="large"
                className="pc-only"
                onClick={() => {
                  navigate(ROUTER_PATH.document.analysis.create.form(uuid));
                }}
              >
                2단계 문서 등록
              </Button>
            ) : (
              <>
                {!isAnalysisInProgress && hasAuthorityToEditTag ? (
                  // 태그 수정 모달
                  <Modal.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
                    <Modal.Trigger>
                      <Button
                        variant="secondary"
                        size="large"
                        className="pc-only"
                      >
                        태그 수정
                      </Button>
                    </Modal.Trigger>
                    <Modal.Layout>
                      <Modal.Content>
                        {documentCategory === "WORK" ? (
                          <WorkTagEditSelect
                            freeTags={freeTags}
                            tags={tags}
                            onClose={handleCloseModal}
                            uuid={uuid}
                            onTagUpdateSuccess={onTagUpdateSuccess}
                            uploadDocPdfAndGetS3Url={uploadDocPdfAndGetS3Url}
                          />
                        ) : (
                          <AnalysisTagEditSelect
                            freeTags={freeTags}
                            tags={tags}
                            onClose={handleCloseModal}
                            uuid={uuid}
                            onTagUpdateSuccess={onTagUpdateSuccess}
                            uploadDocPdfAndGetS3Url={uploadDocPdfAndGetS3Url}
                          />
                        )}
                      </Modal.Content>
                    </Modal.Layout>
                  </Modal.Root>
                ) : undefined}
              </>
            )}

            {/* 문서 삭제 버튼 */}
            {!isAnalysisInProgress && hasAuthorityToDelete ? (
              <Modal.Root
                open={isDeleteModalOpen}
                onOpenChange={setIsDeleteModalOpen}
              >
                <Modal.Trigger asChild>
                  <Button
                    variant="tertiary-neg"
                    size="large"
                    style={{ width: "10rem", whiteSpace: "nowrap" }}
                    className="pc-only"
                  >
                    삭제
                  </Button>
                </Modal.Trigger>
                <Modal.Layout>
                  <Modal.Body
                    title="문서를 삭제하시겠습니까?"
                    onCancel={() => setIsDeleteModalOpen(false)}
                    onConfirm={() => handleDeleteDocument(documentCategory)}
                  ></Modal.Body>
                </Modal.Layout>
              </Modal.Root>
            ) : undefined}
          </>
        }
      />
      <Modal.Root
        open={openReferenceUserEditModal}
        onOpenChange={setOpenReferenceUserEditModal}
      >
        <Modal.Body
          buttonContainerStyle={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
          }}
          style={{
            overflow: "visible",
          }}
          overflowVisible={true}
          onCancel={() => setOpenReferenceUserEditModal(false)}
          onConfirm={() => {
            updateReferenceUser({
              documentUuid: uuid,
              referenceUserUuids: tempReferenceUsersList.map(
                (user) => user.uuid,
              ),
              permissionType,
              currentStatus: approveStatus,
              successCallback: () => {
                setNewReferenceUsersList([...tempReferenceUsersList]);
                setOpenReferenceUserEditModal(false);
              },
            });
          }}
        >
          <ReferenceUserEditModal
            // 젼결권자 제외
            referenceUsers={tempReferenceUsersList}
            onChangeReferenceUsers={(referenceUsers) => {
              setTempReferenceUsersList(referenceUsers);
            }}
            author={author}
            approvedUser={approvedUserUuid}
          />
        </Modal.Body>
      </Modal.Root>
      {isSuccessToast && (
        <Toast.Message open={isSuccessToast} onOpenChange={setIsSuccessToast}>
          파일이 추가 첨부되었습니다.
        </Toast.Message>
      )}
    </>
  );
};
export default DocumentDetailFooterForRegularUser;

const ReferenceUserWrapStyle = styled.div`
  display: flex;
  flex-direction: row;
  gap: 1.25rem;

  ${({ theme }) => theme.media.small} {
    gap: 0.6rem;
  }
`;
